import { DraftingTool } from "./drafting-tools";
import { draftingLabels } from "../shared/drafting";
import { useValue } from "@tldraw/state-react";
import { Check, Crosshair, X } from "lucide-react";
import { scaleSource, sourceMeters, sourceName } from "./source-scale";
import { PrecisionTool } from "./tools";
import type { WorkspaceModel } from "./useWorkspace";
import { useAgentThreads } from "./agent-threads";
import { askAgentShortcut } from "./ask-agent";
export function ToolGuide({ model }: { model: WorkspaceModel }) {
  const { editor, tool, pointCount, busy, active, choose, canEdit } = model;
  const agents = useAgentThreads();
  const start = useValue(
    "calibration start",
    () => (model.startPoint ? editor.pageToScreen(model.startPoint) : null),
    [editor, model.startPoint],
  );
  const labels: Record<string, string> = {
    ...draftingLabels,
    move: "Move selection",
    calibrate: "Set drawing scale",
    line: "Line",
    dimension: "Dimension",
    area: "Hatched area",
    polygon: "Polygon",
    eraser: "Erase whole objects",
    angle: "Angle",
    pin: "Point comment",
    "comment-area": "Area",
    draw: "Pen",
    highlight: "Highlighter",
    geo: "Rectangle",
    cloud: "Revision cloud",
    arrow: "Arrow",
    text: "Text",
    "trace-paper": "Place tracing paper",
    hand: "Move around",
  };
  const hints: Record<string, string> = {
    polyline:
      "Click points · Enter or double-click finishes · Backspace removes a point.",
    circle: "Drag from the centre to set the radius.",
    curve:
      "Click two endpoints, then a control point · Drag handles to adjust.",
    wall: "Click the wall path · Enter or double-click finishes.",
    door: "Click a wall · Width and swing are in settings.",
    window: "Click a wall · Width is in settings.",
    leader:
      "Click a target, then the label position · Edit the label after placing.",
    move: "Drag selected objects together · Escape cancels the move.",
    calibrate: pointCount
      ? "Step 2 of 3 · Click the other end of the known distance."
      : "Step 1 of 3 · Click the first end of a distance you already know.",
    line: pointCount
      ? "Click the end · Snaps near horizontal/vertical · Alt/Option draws freely."
      : "Click anywhere to start · Lines snap near horizontal/vertical.",
    dimension: pointCount
      ? "Click the second point."
      : "Click the first point.",
    area:
      pointCount >= 3
        ? `${pointCount} corners · add more or finish.`
        : pointCount
          ? `${pointCount} ${pointCount === 1 ? "corner" : "corners"} · add ${3 - pointCount} more to finish.`
          : "Click at least three corners.",
    polygon:
      "Click corners · Backspace undoes · Enter or first corner finishes · Esc cancels.",
    eraser: "Drag to erase whole objects · Undo restores them.",
    angle: [
      "Click the first side.",
      "Click the corner.",
      "Click the second side.",
    ][Math.min(pointCount, 2)],
    pin: `Click to place a discussion · object pins move with objects.${
      agents.enabled && canEdit ? ` · ${askAgentShortcut()} asks the agent` : ""
    }`,
    "comment-area":
      "Sweep an area to discuss · review marks stay out of exports.",
    draw: "Drag to sketch · Select when done.",
    highlight: "Drag anywhere to highlight.",
    geo: "Drag out a rectangle.",
    cloud: "Drag around a revision · select it to add a label.",
    arrow: "Drag toward the detail.",
    text: "Click for a wrapping text box · Drag to choose its width · Esc finishes.",
    "trace-paper": "Move over your drawing · Click to place · Esc cancels.",
    hand: "Drag to pan.",
  };
  if (busy)
    return (
      <div
        className="tool-guide compact-tool-guide floating-surface"
        role="status"
      >
        {busy}
      </div>
    );
  if (model.cropping)
    return (
      <div
        className="tool-guide compact-tool-guide floating-surface"
        role="status"
      >
        <div>
          <strong>Crop image</strong>
          <span>Drag edges to crop.</span>
        </div>
        <button onClick={() => model.finishCrop(true)}>Cancel crop</button>
        <button className="primary" onClick={() => model.finishCrop()}>
          Done
        </button>
      </div>
    );
  const source = scaleSource(editor, model.selected[0]) || active;
  const needsScale =
    ["dimension", "area"].includes(tool) &&
    !pointCount &&
    !!source &&
    !sourceMeters(editor, source);
  if (tool === "select" && !needsScale) return null;
  // The trace toolbar already names the destination and provides Done.
  if (
    model.annotation.trace &&
    ["draw", "geo", "arrow", "text", "eraser"].includes(tool)
  )
    return null;
  return (
    <>
      {tool === "calibrate" && start && (
        <span
          className="calibration-anchor"
          style={{ left: start.x, top: start.y }}
        >
          1
        </span>
      )}
      <div
        className="tool-guide compact-tool-guide floating-surface"
        role="status"
      >
        <div>
          <strong>
            {needsScale ? "Set scale" : labels[tool] || "Drawing tool"}
          </strong>
          <span>
            {needsScale
              ? `Use a known distance on ${source ? sourceName(source) : "your drawing"} before measuring.`
              : hints[tool]}
          </span>
        </div>
        {needsScale ? (
          <>
            <button
              className="primary"
              onClick={() => {
                if (source) editor.select(source.id);
                choose("calibrate");
              }}
            >
              <Crosshair size={16} />
              Set scale
            </button>
            <button aria-label="Cancel tool" onClick={() => choose("select")}>
              Cancel
            </button>
          </>
        ) : (
          <>
            {["wall", "polyline"].includes(tool) && (
              <button
                onClick={() => {
                  const current = editor.getCurrentTool();
                  if (current instanceof DraftingTool) current.finish();
                }}
              >
                Finish path
              </button>
            )}
            {["area", "polygon"].includes(tool) && pointCount > 0 && (
              <button
                onClick={() => {
                  const current = editor.getCurrentTool();
                  if (current instanceof PrecisionTool) current.undoCorner();
                }}
              >
                Undo corner
              </button>
            )}
            {["area", "polygon"].includes(tool) && (
              <button
                className="primary"
                disabled={pointCount < 3}
                onClick={() => {
                  const current = editor.getCurrentTool();
                  if (current instanceof PrecisionTool) current.finish();
                }}
              >
                <Check size={16} />
                Finish area
              </button>
            )}
            <button
              className="cancel-tool"
              aria-label="Cancel tool"
              onClick={() => choose("select")}
            >
              <X size={16} />
              <span>Cancel</span>
            </button>
          </>
        )}
      </div>
    </>
  );
}
